import _ from "lodash";
import React, { useMemo, useRef, memo, Fragment } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { Tooltip } from "@toddle-edu/ds-web";
import { SmileyHappyOutlined, AddCircleFilled } from "@toddle-edu/ds-icons";

import { useI18n } from "Hooks";
import { compose } from "lib/apolloClient";
import handleEnterKey from "Utils/handleEnterKey";
import { isIdEqual } from "Utils/idHelpers";
import { toggleReaction } from "MessagingV2/modules/MessagingModule";
import {
  getIsViewMode,
  isMessagingRevempEnabled,
} from "MessagingV2/modules/Utils";

import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import classes from "./MessageReactions.scss";

const styles = {
  overlayInnerStyle: { maxWidth: "320px", textAlign: "center" },
};

const unifiedToNative = unified => {
  const uniCodes = _.split(unified, "-");
  const codePoints = _.map(uniCodes, u => parseInt(u, 16));

  return String.fromCodePoint(...codePoints);
};

const groupReactionsByUid = reactions => {
  const groupedReactions = _.groupBy(reactions, "uid");
  return _.map(groupedReactions, (reactions, uid) => {
    const { unified, skinTone } = _.first(reactions);
    return {
      uid,
      // TODO: change the next two props in future to support multiple skin tones
      unified,
      skinTone,
      totalCount: reactions.length,
      users: _.uniqBy(
        _.map(reactions, reaction => reaction.createdBy),
        "id"
      ),
    };
  });
};

const popoverProps = { positions: ["top", "bottom"], align: "end" };

const MessageReactions = props => {
  const {
    width,
    reactions,
    message,
    toggleReaction,
    setMainEmojiPickerState,
    isNextConsecutiveMessage,
    disableReaction = false,
  } = props;
  const { id: messageId, createdBy } = message;
  const { t } = useI18n(["common"]);
  const {
    chatId,
    screenMode,
    activeUserId: userId,
    isPreviewMessagesModal,
  } = useChatOptions();

  const isViewMode = getIsViewMode({ screenMode });

  const isReactionDisabled = isViewMode || disableReaction;

  const addMoreButtonRef = useRef(null);
  const isSender = isIdEqual(_.get(createdBy, "id"), userId);

  // group reactions by uid of emoji(eg: "+1")
  const groupedReactions = useMemo(() => groupReactionsByUid(reactions), [
    reactions,
  ]);

  const onReactionClick = reaction => {
    // this is actually the reaction object, not the emoji object
    // by emoji-mart, so we need to convert it to emoji-mart format
    const { uid, unified, skinTone } = reaction;
    toggleReaction({
      messageId,
      emojiObject: { id: uid, unified, skin: skinTone },
      chatId,
    });
  };

  const toggleMainEmojiPicker = () => {
    setMainEmojiPickerState(prev =>
      prev
        ? null
        : {
            message,
            element: addMoreButtonRef.current,
            popoverProps,
          }
    );
  };

  const containerClasses = classNames(classes.container, {
    [classes.senderContainer]: isSender,
    [classes.extraMargin]: isNextConsecutiveMessage,
  });

  // set dynamic width of reactions container based on bubble width
  const style = { width };

  return (
    <div
      className={
        isMessagingRevempEnabled() ? classes.containerV2 : containerClasses
      }
      style={style}
    >
      {_.map(groupedReactions, reaction => {
        const { uid, unified, totalCount, users } = reaction;
        const isUserReacted = _.some(users, user => isIdEqual(user.id, userId));

        const tooltip = t("common:join_array_with_and", {
          array: _.map(users, user =>
            isIdEqual(user.id, userId) ? t("common:you") : user.firstName
          ),
        });

        const emojiContainerClasses = classNames(
          "text-body-2",
          classes.emojiContainer,
          {
            [classes.reactedBoundary]: isUserReacted,
            [classes.plainBoundary]: !isUserReacted,
            [classes.plainBoundaryHover]: !isUserReacted && !isReactionDisabled,
          }
        );

        const handleAction = () => onReactionClick(reaction);
        const ariaLabel = `${isUserReacted ? "remove" : "add"} ${uid} reaction`;

        const content = (
          <div
            tabIndex={isReactionDisabled ? null : 0}
            role={isReactionDisabled ? null : "button"}
            aria-label={ariaLabel}
            onClick={isReactionDisabled ? null : handleAction}
            onKeyDown={isReactionDisabled ? null : handleEnterKey(handleAction)}
            className={emojiContainerClasses}
          >
            <div
              className={classNames(classes.totalCountTextClass, "text-body-1")}
            >
              {totalCount}
            </div>
            <div className={classNames(classes.emoji, "text-body-1")}>
              {unifiedToNative(unified)}
            </div>
          </div>
        );

        if (isPreviewMessagesModal) {
          return <Fragment key={uid}>{content}</Fragment>;
        }

        return (
          <Tooltip
            key={uid}
            placement="top"
            tooltip={<div className="text-tooltip">{tooltip}</div>}
            overlayInnerStyle={styles.overlayInnerStyle}
          >
            {content}
          </Tooltip>
        );
      })}
      {!isReactionDisabled ? (
        <div
          tabIndex={0}
          role="button"
          ref={addMoreButtonRef}
          aria-label="open reactions panel"
          onClick={toggleMainEmojiPicker}
          onKeyDown={handleEnterKey(toggleMainEmojiPicker)}
          className={classNames(
            classes.addIconWrapper,
            classes.plainBoundary,
            classes.plainBoundaryHover
          )}
        >
          <div className={classes.iconRelativeContainer}>
            <SmileyHappyOutlined size="xx-small" variant="subtle" />
            <div className={classes.absoluteIconContainer}>
              <AddCircleFilled size="xxxx-small" variant="subtle" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

MessageReactions.propTypes = {
  width: PropTypes.number.isRequired,
  reactions: PropTypes.array.isRequired,
  message: PropTypes.object.isRequired,
  toggleReaction: PropTypes.func.isRequired,
  setMainEmojiPickerState: PropTypes.func.isRequired,
  isNextConsecutiveMessage: PropTypes.bool.isRequired,
};

const mapDispatchToProps = { toggleReaction };

export default compose(
  connect(null, mapDispatchToProps),
  memo
)(MessageReactions);
